// Page identity, independent of how the host serves URLs.
//
// The engine routes by page name (bonus1, thankyou, ...). It used to read that
// straight off the pathname, which meant it only worked when the host served
// literal `.html` URLs. Hosts that serve extensionless routes (Cloudflare
// Workers Assets, Netlify, Vercel) broke that, and funnels worked around it by
// rewriting the address bar client-side with history.replaceState — which is a
// bad look on a payment page. Normalising here removes the need for that hack.
//
// Pure module: no DOM, no jQuery, no config. Keep it that way so it stays
// cheap to test.

const DEFAULT_PAGE = 'index';

// '/apgt1/checkout/bonus1.html' -> 'bonus1'
// '/apgt1/checkout/bonus1'      -> 'bonus1'
// '/apgt1/checkout/'            -> 'index'
export function normalizePageName(pathname) {
	if (!pathname) return DEFAULT_PAGE;

	const fileName = pathname.substring(pathname.lastIndexOf('/') + 1);
	if (!fileName) return DEFAULT_PAGE;

	// Strip a single trailing extension only, so 'bonus1.min.html' -> 'bonus1.min'.
	const lastDot = fileName.lastIndexOf('.');
	const bare = lastDot > 0 ? fileName.substring(0, lastDot) : fileName;

	return bare.toLowerCase();
}

// Bonus pages are numbered. The previous implementation took the substring
// between 'bonus' and '.html' and parseInt'd it, which quietly tolerated
// suffixes ('bonus1-alt' -> 1). Keep that leniency: some funnels use variants.
export function getBonusNumber(pageName) {
	if (!pageName) return null;
	const match = /bonus(\d+)/.exec(pageName);
	return match ? parseInt(match[1], 10) : null;
}

// Build an in-funnel link. Funnels on extensionless hosts set pageExtension: ''
// so the engine stops emitting links that only work via a host redirect.
export function buildPageUrl(pageName, extension = '.html') {
	return `${pageName}${extension}`;
}

// Checkout pages live one directory below the funnel root
// ('<funnel>/checkout/bonus1.html'), so that segment is not a funnel of its own.
const CHECKOUT_DIR = 'checkout';

// Which funnel a URL belongs to.
//
// sessionStorage is per-ORIGIN, but a single domain routinely hosts several
// funnels ('/' and '/ngos1/'). Anything the engine keeps per-purchase-journey
// therefore has to be keyed by funnel, or the funnels read each other's state.
//
// '/checkout/bonus1.html'       -> '/'
// '/shipping.html'              -> '/'
// '/ngos1/index.html'           -> '/ngos1/'
// '/ngos1/checkout/bonus1.html' -> '/ngos1/'
export function getFunnelScope(pathname) {
	if (!pathname) return '/';

	const dir = pathname.substring(0, pathname.lastIndexOf('/') + 1);
	const segments = dir.split('/').filter(Boolean);

	if (segments[segments.length - 1] === CHECKOUT_DIR) {
		segments.pop();
	}

	return segments.length ? `/${segments.join('/')}/` : '/';
}
